import type React from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import playedTogetherApi, { type PlayedTogetherItem } from '@showliner/shared/services/playedTogetherApi';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getAssetUrl } from '@/utils/crossAppLinks';

interface PlayedTogetherProps {
  entityType: 'performer' | 'venue';
  entityId: string;
}

/** How many sit on the page before "Show all". */
const PREVIEW = 6;

const showsLabel = (n: number): string => (n === 1 ? '1 show' : `${n} shows`);

/**
 * "Shared the stage with" on an act's public profile, "Played here" on a
 * venue's (issue #41 phase 3, ADR-0045). A completed, public show is public,
 * and so is its bill, so this lists who the profile played with and how often,
 * never which show, when or where. The server leaves out hidden links and acts
 * that turned public discovery off. Renders nothing when there is nothing.
 */
const PlayedTogether: React.FC<PlayedTogetherProps> = ({ entityType, entityId }) => {
  const [items, setItems] = useState<PlayedTogetherItem[]>([]);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    playedTogetherApi
      .get(entityType, entityId)
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
      })
      .catch(() => { /* Nothing to show. */ });
    return () => { cancelled = true; };
  }, [entityType, entityId]);

  if (items.length === 0) return null;

  const visible = expanded ? items : items.slice(0, PREVIEW);
  const more = items.length > PREVIEW;

  return (
    <div className="w-full rounded-lg border border-border/50 overflow-hidden">
      <div className="px-4 py-3 bg-muted/30 border-b border-border/50 flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-grey-900">
          {entityType === 'venue' ? 'Played here' : 'Shared the stage with'}
        </h3>
        {total > items.length && <span className="text-xs text-muted-foreground">{total} in all</span>}
      </div>
      <div className="divide-y divide-border/30">
        {visible.map((item) => (
          <Link
            key={item.id}
            to={`/${item.type}/${item.id}`}
            className="flex items-center gap-3 px-4 py-3 hover:bg-muted/20 transition-colors"
          >
            <Avatar className="h-9 w-9">
              {item.picture && <AvatarImage src={getAssetUrl(item.picture)} alt="" />}
              <AvatarFallback className="text-xs">{item.name.slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <span className="flex-1 min-w-0 text-sm font-medium text-grey-900 truncate">{item.name}</span>
            <span className="text-xs text-muted-foreground">{showsLabel(item.shows)}</span>
          </Link>
        ))}
      </div>
      {more && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="w-full px-4 py-2.5 text-xs font-medium text-primary hover:bg-muted/20 transition-colors flex items-center justify-center gap-1"
        >
          {expanded ? (
            <><ChevronUp className="h-3.5 w-3.5" /> Show less</>
          ) : (
            <><ChevronDown className="h-3.5 w-3.5" /> Show all {items.length}</>
          )}
        </button>
      )}
    </div>
  );
};

export default PlayedTogether;
